import { useMemo } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import type { SummaryListingDetails } from '@/src/types/summaryListingDetails.type';
import type { ListingMinMaxPriceBeds } from '@/src/types/commonTypes.type';
import type { ModelSummary } from '@/src/components/ListingCard/types';
import StringConstants from '@/src/lib/StringConstants';
import { getPropertyTypeLabelKey } from '@/src/lib/utils/listingPropertyType';

/**
 * Locale-aware listing formatter helpers. Returns memoized functions
 * keyed on the active locale so price/beds/baths/sqft strings flip with
 * the language toggle. Use this hook in React components; there is no
 * non-React counterpart (the pure helpers in `listingFormatters.ts` were
 * removed once it was clear nothing called them).
 */
export function useListingFormatters() {
  const tListing = useTranslations('listing');
  const tHdp = useTranslations('hdp');
  const tCommon = useTranslations('common');
  const locale = useLocale();
  const numberLocale = locale === 'es' ? 'es' : 'en-US';

  return useMemo(() => {
    const contactForPrice = tCommon('contactForPrice');
    const noAddressAvailable = tHdp('noAddressAvailable');

    const fmt$ = (n: number) => `$${n.toLocaleString(numberLocale)}`;

    const priceFromMinMax = (info: { minPrice?: number; maxPrice?: number } | undefined): string | null => {
      if (info?.minPrice === undefined || info.maxPrice === undefined) return null;
      if (info.minPrice <= 0) return null;
      if (info.minPrice === info.maxPrice) return fmt$(info.minPrice);
      return `${fmt$(info.minPrice)}+`;
    };

    const formatPrice = (listing: {
      price?: number;
      listingMinMaxPriceBeds?: ListingMinMaxPriceBeds;
      modelSummary?: ModelSummary;
    }): string => {
      if (listing.price !== undefined && listing.price > 0) return fmt$(listing.price);

      const priceInfo = listing.listingMinMaxPriceBeds;

      // The numeric fields win over the preformatted priceDisplay, which can
      // embed a "min - max" range that no longer matches the collapsed display.
      const fromNumbers = priceFromMinMax(priceInfo) ?? priceFromMinMax(listing.modelSummary);
      if (fromNumbers) return fromNumbers;

      if (priceInfo?.priceDisplay && priceInfo.priceDisplay !== '$0') return priceInfo.priceDisplay;

      return contactForPrice;
    };

    const bedSingle = (count: number) => {
      if (count === 0) return tListing('studio');
      return tListing('bed', { count });
    };

    const bedRangeText = (minBeds: number, maxBeds: number) => {
      if (minBeds === maxBeds) return bedSingle(minBeds);
      if (minBeds === 0) {
        return maxBeds === 1
          ? tHdp('studioOneBedRange')
          : tHdp('studioRange', { max: maxBeds });
      }
      return tHdp('bedRange', { min: minBeds, max: maxBeds });
    };

    const formatBedrooms = (listing: {
      bedrooms?: number;
      listingMinMaxPriceBeds?: ListingMinMaxPriceBeds;
      modelSummary?: ModelSummary;
    }): string | null => {
      if (listing.bedrooms !== undefined) return bedSingle(listing.bedrooms);

      const priceInfo = listing.listingMinMaxPriceBeds;
      if (priceInfo?.bedDisplay) return priceInfo.bedDisplay;
      if (priceInfo?.minBeds !== undefined && priceInfo?.maxBeds !== undefined) {
        return bedRangeText(priceInfo.minBeds, priceInfo.maxBeds);
      }

      const modelInfo = listing.modelSummary;
      if (modelInfo?.minBeds !== undefined && modelInfo?.maxBeds !== undefined) {
        return bedRangeText(modelInfo.minBeds, modelInfo.maxBeds);
      }

      return null;
    };

    const bathFromRange = (min: number, max: number): string => {
      if (max === min) return tListing('bath', { count: min });
      return tHdp('bathRangeListing', { min, max });
    };

    const formatBathrooms = (listing: {
      bathrooms?: number;
      listingMinMaxPriceBeds?: ListingMinMaxPriceBeds;
      modelSummary?: ModelSummary;
    }): string | null => {
      if (listing.bathrooms !== undefined) return tListing('bath', { count: listing.bathrooms });

      const priceInfo = listing.listingMinMaxPriceBeds;
      if (priceInfo?.bathDisplay) return priceInfo.bathDisplay;
      if (priceInfo?.minBaths !== undefined && priceInfo?.maxBaths !== undefined) {
        return bathFromRange(priceInfo.minBaths, priceInfo.maxBaths);
      }

      const modelInfo = listing.modelSummary;
      if (modelInfo?.minBaths !== undefined) {
        if (modelInfo.maxBaths !== undefined && modelInfo.minBaths !== modelInfo.maxBaths) {
          return bathFromRange(modelInfo.minBaths, modelInfo.maxBaths);
        }
        return tListing('bath', { count: modelInfo.minBaths });
      }

      return null;
    };

    // Returns just the count portion of the bed spec, suitable as visible
    // (icon-adjacent) text when the full "X bed(s)" phrase is delegated to
    // a screen-reader-only sibling. Falls back to null when the API only
    // supplied a preformatted display string we can't safely split.
    const formatBedroomsValue = (listing: {
      bedrooms?: number;
      listingMinMaxPriceBeds?: ListingMinMaxPriceBeds;
      modelSummary?: ModelSummary;
    }): string | null => {
      const studio = tListing('studio');
      const fromRange = (min: number, max: number): string => {
        if (min === max) return min === 0 ? studio : String(min);
        if (min === 0) return `${studio}-${max}`;
        return `${min}-${max}`;
      };

      if (listing.bedrooms !== undefined) {
        return listing.bedrooms === 0 ? studio : String(listing.bedrooms);
      }

      const priceInfo = listing.listingMinMaxPriceBeds;
      if (priceInfo?.minBeds !== undefined && priceInfo?.maxBeds !== undefined) {
        return fromRange(priceInfo.minBeds, priceInfo.maxBeds);
      }

      const modelInfo = listing.modelSummary;
      if (modelInfo?.minBeds !== undefined && modelInfo?.maxBeds !== undefined) {
        return fromRange(modelInfo.minBeds, modelInfo.maxBeds);
      }

      return null;
    };

    const formatBathroomsValue = (listing: {
      bathrooms?: number;
      listingMinMaxPriceBeds?: ListingMinMaxPriceBeds;
      modelSummary?: ModelSummary;
    }): string | null => {
      const rangeText = (min: number, max: number): string =>
        min === max ? String(min) : `${min}-${max}`;

      if (listing.bathrooms !== undefined) return String(listing.bathrooms);

      const priceInfo = listing.listingMinMaxPriceBeds;
      if (priceInfo?.minBaths !== undefined && priceInfo?.maxBaths !== undefined) {
        return rangeText(priceInfo.minBaths, priceInfo.maxBaths);
      }

      const modelInfo = listing.modelSummary;
      if (modelInfo?.minBaths !== undefined) {
        if (modelInfo.maxBaths !== undefined) {
          return rangeText(modelInfo.minBaths, modelInfo.maxBaths);
        }
        return String(modelInfo.minBaths);
      }

      return null;
    };

    // Sqft has only a numeric form (no "Studio" analog). Returns just the
    // value with locale-aware formatting and no unit suffix — the SR-only
    // counterpart (formatSqft) carries "sq ft" via i18n.
    const formatSqftValue = (listing: {
      sqft?: number;
      listingMinMaxPriceBeds?: ListingMinMaxPriceBeds;
      modelSummary?: ModelSummary;
    }): string | null => {
      const fmt = (n: number) => n.toLocaleString(numberLocale);

      if (listing.sqft !== undefined) return fmt(listing.sqft);

      const modelInfo = listing.modelSummary;
      if (modelInfo?.minSqft !== undefined) {
        if (modelInfo.maxSqft !== undefined && modelInfo.minSqft !== modelInfo.maxSqft) {
          return `${fmt(modelInfo.minSqft)}-${fmt(modelInfo.maxSqft)}`;
        }
        return fmt(modelInfo.minSqft);
      }

      return null;
    };

    const formatSqft = (listing: {
      sqft?: number;
      listingMinMaxPriceBeds?: ListingMinMaxPriceBeds;
      modelSummary?: ModelSummary;
    }): string | null => {
      if (listing.sqft !== undefined) return tHdp('sqftDisplay', { value: listing.sqft });

      const priceInfo = listing.listingMinMaxPriceBeds;
      if (priceInfo?.sqftDisplay) return priceInfo.sqftDisplay;

      const modelInfo = listing.modelSummary;
      if (modelInfo?.minSqft !== undefined) {
        if (modelInfo.maxSqft !== undefined && modelInfo.minSqft !== modelInfo.maxSqft) {
          return tHdp('sqftRangeDisplay', { min: modelInfo.minSqft, max: modelInfo.maxSqft });
        }
        return tHdp('sqftDisplay', { value: modelInfo.minSqft });
      }

      return null;
    };

    const formatSpecs = (listing: {
      bedrooms?: number;
      bathrooms?: number;
      sqft?: number;
      listingMinMaxPriceBeds?: ListingMinMaxPriceBeds;
      modelSummary?: ModelSummary;
    }): string => {
      const parts: Array<string> = [];
      const b = formatBedrooms(listing);
      if (b) parts.push(b);
      const ba = formatBathrooms(listing);
      if (ba) parts.push(ba);
      const s = formatSqft(listing);
      if (s) parts.push(s);
      return parts.join(' • ');
    };

    const formatPropertyType = (listing: SummaryListingDetails): string | null => {
      const propertyType = listing.propertyType;
      const address = listing.address;
      if (!address) return null;

      const city = address.city || '';
      const state = address.state || '';
      const location = [city, state].filter(Boolean).join(', ');
      if (!location) return null;

      const type = tListing(getPropertyTypeLabelKey(propertyType));
      const prefix = listing.listingType === 'room' ? StringConstants.R4R_PREFIX : '';
      return `${prefix}${tHdp('propertyTypeIn', { type, location })}`;
    };

    const formatAddress = (
      address?: string | { street?: string; city?: string; state?: string; zip?: string },
    ): string => {
      if (!address) return noAddressAvailable;
      if (typeof address === 'string') return address;
      return address.street || noAddressAvailable;
    };

    return {
      formatPrice,
      formatBedrooms,
      formatBathrooms,
      formatBedroomsValue,
      formatBathroomsValue,
      formatSqftValue,
      formatSqft,
      formatSpecs,
      formatPropertyType,
      formatAddress,
      contactForPrice,
      tCommon,
    };
  }, [tListing, tHdp, tCommon, numberLocale]);
}
